import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Subscription } from 'rxjs';

import { ModifiedErrorStateMatcher } from '../../extensions/errorstatematcher/errorstatematcher';

@Component({
    selector: 'material-textarea',
    templateUrl: './textarea.component.html',
    styleUrls: ['./textarea.component.scss'],
    standalone: false,
})
export class MaterialTextAreaComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
    @Input() placeholder;
    @Input() name;
    @Input() title;
    @Input() model;
    @Input() id;
    @Input() classList;
    @Input() debounceAfter: number;
    @Input() options: any[];
    @Input() autoComplete: boolean;
    @Input() required: boolean;
    @Input() errors: any[];
    @Input() warnings: any[];
    @Input() disabled: boolean;
    @Input() readonly: boolean;
    @Input() type: string;
    @Input() matTooltip: string;
    @Input() hint: string;
    @Input() maxLength: number;
    @Input() needIcon: boolean;
    @Input() description: string;
    @Input() rows: number;
    @Input() appearance: MatFormFieldAppearance;

    @Output() modelChange = new EventEmitter();
    @Output() debounceEventHandler = new EventEmitter();
    @Output() keyUpEventHandler = new EventEmitter();
    @Output() onDataClear = new EventEmitter();

    matcher = new ModifiedErrorStateMatcher();
    control = new UntypedFormControl('');
    valueSubscription: Subscription;
    computedClassList = '';
    debounceValue = 1000;
    characterCount = 0;
    minRows = 1;
    private lastErrorsLength: number | undefined;

    constructor() {}

    ngDoCheck(): void {
        // Lightweight sync: if the errors array length changed (possibly mutated in place), update control state
        const currentLen = Array.isArray(this.errors) ? this.errors.length : 0;
        if (this.lastErrorsLength !== currentLen) {
            this.updateFormErrors();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        if (changes.model) {
            this.control.setValue(this.model);
        }
        if (changes.disabled) {
            if (changes.disabled.currentValue === true) {
                this.control.disable();
            } else {
                this.control.enable();
            }
        }
        if (changes.errors) {
            this.updateFormErrors();
        }
        this.updateComputedProperties();
    }

    modelChanged = _event => {
        this.model = _event;
        this.modelChange.next(this.model);
    };

    clearData = value => {
        this.onDataClear.emit(value);
    };

    // keyupEvent = (_event) => {
    //     this.keyupEventHandler.emit(_event);
    // }

    inputChange = (data: any) => {
        if (!data) {
            return;
        }
        const value = data.$event?.target?.value || data.target?.value;
        if (value !== undefined) {
            this.control.setValue(value);
        }
    };

    ngOnInit() {
        this.valueSubscription = this.control.valueChanges.subscribe(_event => {
            this.modelChanged(_event);
            this.updateCharacterCount();
        });
        this.updateComputedProperties();
        this.updateFormErrors();
    }

    private updateFormErrors(): void {
        // Sync external errors input to form control state without heavy comparisons
        if (!this.errors || (Array.isArray(this.errors) && this.errors.length === 0)) {
            if (this.control.getError('errors') !== undefined) {
                this.control.setErrors(undefined);
            }
            this.lastErrorsLength = 0;
        } else {
            this.control.setErrors({ errors: this.errors });
            this.lastErrorsLength = this.errors.length;
        }
    }

    private updateComputedProperties(): void {
        this.computedClassList = this.classList?.join(' ') || '';
        this.debounceValue = this.debounceAfter || 1000;
        this.minRows = this.rows || 1;
        this.updateCharacterCount();
    }

    private updateCharacterCount(): void {
        this.characterCount = (this.control.value && this.control.value.length) || 0;
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.valueSubscription && this.valueSubscription.unsubscribe();
    }
}
